// BibliographyPickerDialog — multi-select My Library items to attach as
// references on the current piece, then insert an `excerptus.bibliography`
// node at the cursor. Sorted alphabetically by author Last, First at render
// time via the bibliography block renderer.
import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

type LibraryItem = {
  id: string;
  title: string;
  author_text: string | null;
  authors: string[] | null;
  published_year: number | null;
};

const firstAuthor = (item: LibraryItem): string => {
  if (Array.isArray(item.authors) && item.authors.length > 0) return String(item.authors[0]);
  if (item.author_text) return item.author_text.split(/,| and /i)[0]?.trim() ?? "";
  return "";
};

const authorLastFirst = (raw: string): string => {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  const space = trimmed.lastIndexOf(" ");
  if (space === -1) return trimmed;
  return `${trimmed.slice(space + 1)}, ${trimmed.slice(0, space)}`;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string | null;
  pieceId: string | null;
  /** Called after references are persisted; parent then inserts the bibliography node. */
  onInserted: () => void;
  /**
   * Initial `cited` flag applied to entries added through this dialog.
   * Defaults to `false` because the dialog's verb is "Add to bibliography" —
   * background-reading-only is the expected default. Pass `true` for callers
   * that explicitly want inline citation behavior.
   */
  defaultCited?: boolean;
};

export const BibliographyPickerDialog = ({
  open,
  onOpenChange,
  workspaceId,
  pieceId,
  onInserted,
  defaultCited = false,
}: Props) => {
  const [items, setItems] = useState<LibraryItem[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !workspaceId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      const { data, error } = await supabase
        .from("library_items")
        .select("id,title,author_text,authors,published_year")
        .eq("workspace_id", workspaceId)
        .order("title", { ascending: true })
        .limit(500);
      if (cancelled) return;
      if (error) {
        toast.error("Failed to load My Library");
      } else {
        setItems(((data ?? []) as unknown) as LibraryItem[]);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [open, workspaceId]);

  useEffect(() => {
    if (!open) setSelected(new Set());
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) =>
      [i.title, i.author_text, ...(i.authors ?? [])].filter(Boolean).join(" ").toLowerCase().includes(q),
    );
  }, [items, query]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleConfirm = async () => {
    if (!workspaceId || !pieceId || selected.size === 0) {
      onOpenChange(false);
      return;
    }
    setBusy(true);
    try {
      const payload = Array.from(selected).map((id, i) => {
        const item = items.find((x) => x.id === id);
        const author = item ? authorLastFirst(firstAuthor(item)) : "";
        const year = item?.published_year ? ` (${item.published_year})` : "";
        const label = item
          ? (author ? `${author}${year}. ${item.title}` : `${item.title}${year}`)
          : "";
        return {
          workspace_id: workspaceId,
          piece_id: pieceId,
          // "source" is the reference_type used for My Library items in the
          // existing references table.
          reference_type: "source" as const,
          reference_id: id,
          label_snapshot: label,
          sort_order: i,
          cited: defaultCited,
        };
      });
      const { error } = await supabase
        .from("content_piece_references")
        .insert(payload);
      if (error) throw new Error(error.message);
      toast.success(`Added ${selected.size} reference${selected.size === 1 ? "" : "s"} to bibliography`);
      onInserted();
      onOpenChange(false);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add bibliography entries</DialogTitle>
        </DialogHeader>
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search My Library…"
          className="h-9"
        />
        <ScrollArea className="h-72 rounded-md border">
          {loading ? (
            <div className="p-4 text-sm text-muted-foreground">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground">
              {items.length === 0 ? "Your My Library is empty." : "No matches."}
            </div>
          ) : (
            <ul className="divide-y">
              {filtered.map((item) => {
                const isSel = selected.has(item.id);
                const author = authorLastFirst(firstAuthor(item));
                return (
                  <li
                    key={item.id}
                    className="flex cursor-pointer items-start gap-3 px-3 py-2 hover:bg-muted/50"
                    onClick={() => toggle(item.id)}
                  >
                    <Checkbox checked={isSel} onCheckedChange={() => toggle(item.id)} className="mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">{item.title}</div>
                      {author && <div className="truncate text-xs text-muted-foreground">{author}</div>}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </ScrollArea>
        <DialogFooter>
          <span className="mr-auto self-center text-xs text-muted-foreground">
            {selected.size} selected
          </span>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={busy || selected.size === 0 || !pieceId}>
            {busy ? "Adding…" : "Add to bibliography"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
